import { useCallback, useEffect, useRef, useState } from "react";
import { useAni } from "@/context/AniContext";
import { useNotification } from "@/context/NotificationContext";
import { getOrCreateSessionId } from "@/hooks/useFeAgentSession";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { CompanyDemoJourney, CompanyDemoPackActive, DemoJourneyEvent, DemoJourneyMode, DemoJourneyState } from "@/services/api/agentic-v2";
import { escapeHtml } from "@/utils/sanitizeHtml";
import { dispatchOpenworkHomeRefresh } from "./openworkDemoState";

const POLL_MS = 2_500;
const SEEN_STORAGE_KEY = "demo-journey-seen";
const ACTION_PREFIX = "journey:";
/** Journey engine keys that mark the presenter script finished (see ``demo_journeys.STAGE_DONE``). */
const DEMO_SCRIPT_END_KEYS = new Set(["done", "closed", "recap"]);

type JourneyAction = DemoJourneyMode;

const GUIDED_JOURNEYS_SECTION = "Guided journeys";

export type JourneyMenuRow = {
  value: string;
  label: string;
  displayLabel: string;
  description: string;
  section: string;
  requirementCode?: string;
};

type DemoJourneySimulate = NonNullable<DemoJourneyEvent["simulate"]>;

type SelectedJourney = { journey: CompanyDemoJourney; mode: DemoJourneyMode };

const MODE_LABELS: Record<DemoJourneyMode, { label: string; description: string }> = {
  scripted: { label: "Scripted", description: "Same every time and quick to run." },
  live: { label: "Live", description: "Real workers run every step. Slower; good for testing the missions." },
};

const startsFromFile = (journey: CompanyDemoJourney) => journey.accepts_files.length > 0;

function journeyFlowLabel(journey: CompanyDemoJourney): string {
  const fromApi = journey.menu_label?.trim();
  if (fromApi) return fromApi;
  return journey.title.replace(/^Demo:\s*/i, "").trim();
}

/** Demo Bar rows for every guided journey the active pack offers. */
export function journeyMenuRows(pack: CompanyDemoPackActive | null): JourneyMenuRow[] {
  return (pack?.journeys ?? []).flatMap((journey) => {
    const flow = journeyFlowLabel(journey);
    const code = journey.requirement_code?.trim() || undefined;
    const summary =
      journey.menu_summary?.trim() ||
      journey.description.trim();
    const row = (action: JourneyAction, label: string, description: string): JourneyMenuRow => ({
      value: `${ACTION_PREFIX}${action}:${journey.id}`,
      label,
      displayLabel: label,
      description,
      section: GUIDED_JOURNEYS_SECTION,
      requirementCode: code,
    });
    const startDescription = startsFromFile(journey)
      ? `${summary} Run, then follow Ani in chat (drop the email to start).`
      : `${summary} Run to start — follow Ani in chat.`;
    return journey.modes.map((mode) =>
      row(mode, flow, mode === "scripted" ? startDescription : `${summary} ${MODE_LABELS[mode].description}`),
    );
  });
}

function journeySampleFileDownloadHref(journeyId: string): string {
  return AgenticV2API.demoJourneySampleFileDownloadHref(journeyId);
}

function fileJourneyReadyMessage(journey: CompanyDemoJourney): string {
  const flow = journeyFlowLabel(journey);
  const sampleName = journey.sample_file_name ?? "sample.eml";
  const sampleHref = journeySampleFileDownloadHref(journey.id);
  return [
    `<p><strong>${escapeHtml(flow)}</strong> is ready.</p>`,
    `<p>Drag the enquiry email onto me to start.</p>`,
    `<p>No file to hand? <a href="${escapeHtml(sampleHref)}" download="${escapeHtml(sampleName)}">Download sample email</a>, then drop it here.</p>`,
  ].join("");
}

export function isJourneyMenuValue(value: string): boolean {
  return value.startsWith(ACTION_PREFIX);
}

function readSeen(): Set<string> {
  try {
    return new Set(JSON.parse(window.sessionStorage.getItem(SEEN_STORAGE_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/**
 * Runs the presenter's guided journey: polls the server while it is active and turns each
 * new event into a toast plus an Ani announcement. A journey that starts from a file is
 * selected first; Ani then explains how to start it and accepts the drop.
 */
export function useDemoJourney(pack: CompanyDemoPackActive | null) {
  const { addNotification, showError } = useNotification();
  const { announceToAni, setAniFileDropHandler, requestNewSession } = useAni();
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState<SelectedJourney | null>(null);
  const seenRef = useRef<Set<string> | null>(null);
  const syncingRef = useRef(false);
  const simulateRef = useRef<(simulate: DemoJourneySimulate) => void>(() => undefined);

  const present = useCallback(
    (event: DemoJourneyEvent) => {
      const simulate = event.simulate;
      const scriptEnd = DEMO_SCRIPT_END_KEYS.has(event.key);
      addNotification({
        type: scriptEnd ? "success" : "info",
        title: scriptEnd ? `Demo complete — ${event.title}` : event.title,
        message: event.body || undefined,
        autoClose: !simulate && !scriptEnd,
        duration: scriptEnd ? 14_000 : 6_000,
        action: simulate ? { label: simulate.label, onClick: () => simulateRef.current(simulate) } : undefined,
      });
      dispatchOpenworkHomeRefresh();
      announceToAni({ id: event.id, role: "assistant", content: event.ani, toolCall: event.open });
    },
    [addNotification, announceToAni],
  );

  const applyState = useCallback(
    (state: DemoJourneyState, showNew: boolean) => {
      seenRef.current ??= readSeen();
      const seen = seenRef.current;
      for (const event of state.events) {
        if (seen.has(event.id)) continue;
        seen.add(event.id);
        if (showNew) present(event);
      }
      window.sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...seen]));
      setActive(state.active);
    },
    [present],
  );

  /** Opens a new Ani chat and resolves once its session id is current, so the journey's lines go there. */
  const openFreshChat = useCallback(async () => {
    const previous = getOrCreateSessionId();
    requestNewSession();
    for (let tries = 0; getOrCreateSessionId() === previous && tries < 40; tries += 1) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }, [requestNewSession]);

  simulateRef.current = (simulate: DemoJourneySimulate) => {
    void AgenticV2API.simulateDemoJourney(simulate.action)
      .then((state) => applyState(state, true))
      .catch(() =>
        addNotification({
          type: "error",
          title: "Demo step failed",
          message: "Try again in a moment.",
          autoClose: false,
          action: { label: `Retry: ${simulate.label}`, onClick: () => simulateRef.current(simulate) },
        }),
      );
  };

  const start = useCallback(
    async (journeyId: string, mode: DemoJourneyMode, file?: File) => {
      const uploadMessageId = file ? `drop-${crypto.randomUUID()}` : undefined;
      if (file && uploadMessageId) announceToAni({ id: uploadMessageId, role: "user", content: `📎 ${file.name}` });
      applyState(await AgenticV2API.startDemoJourney(journeyId, { mode, chatSessionId: getOrCreateSessionId(), file, uploadMessageId }), true);
    },
    [announceToAni, applyState],
  );

  /** Runs a Demo Bar journey row; returns the status line to show. */
  const runMenuAction = useCallback(
    async (value: string): Promise<string> => {
      const [action, journeyId] = value.slice(ACTION_PREFIX.length).split(":") as [JourneyAction, string];
      const journey = pack?.journeys.find((row) => row.id === journeyId);
      if (!journey) throw new Error("Demo not found");
      await openFreshChat();
      if (startsFromFile(journey)) {
        setSelected({ journey, mode: action });
        announceToAni({
          id: `select-${journeyId}-${Date.now()}`,
          role: "assistant",
          content: fileJourneyReadyMessage(journey),
        });
        return "Ready — see Ani";
      }
      await start(journeyId, action);
      return "Started — watch Ani";
    },
    [announceToAni, applyState, openFreshChat, pack, start],
  );

  useEffect(() => {
    if (!pack?.journeys.length) return;
    // A fresh tab marks history as seen rather than replaying every past event.
    void AgenticV2API.getDemoJourney()
      .then((state) => applyState(state, seenRef.current !== null || readSeen().size > 0))
      .catch(() => undefined);
  }, [pack, applyState]);

  useEffect(() => {
    if (!selected) return;
    const { journey, mode } = selected;
    setAniFileDropHandler({
      label: `Drop to start ${journey.title} (${MODE_LABELS[mode].label})`,
      accepts: journey.accepts_files,
      onFile: (file) =>
        start(journey.id, mode, file)
          .then(() => setSelected(null))
          .catch(() => {
            showError("Couldn't start the demo", "Check the file and try again.");
          }),
    });
    return () => setAniFileDropHandler(null);
  }, [selected, setAniFileDropHandler, showError, start]);

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      void AgenticV2API.syncDemoJourney()
        .then((state) => applyState(state, true))
        .catch((error) => console.warn("[DemoJourney] sync failed:", error))
        .finally(() => {
          syncingRef.current = false;
        });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [active, applyState]);

  return { runMenuAction };
}
